import { useEffect, useRef } from 'react';
import { Phone, MessageSquare, PenSquare, Forward, Map, Globe, Copy } from 'lucide-react';
import { handleTrapKey } from '@/lib/focus-trap';
import type { ContactLink } from '@/lib/contact-link';

// The small menu a tapped phone number or address opens in an email body (2026-10-19, Brian).
// Phone: Call / Text. Address: New message to it / Forward an email to it.
// Street address (2026-10-30, Brian): Apple Maps / Google Maps / Copy.
//
// 🚩 Rendered by MessageView OUTSIDE the email box: that box has `contain: paint` (B-22), which
// clips anything inside it, and its innerHTML is replaced whenever the body re-renders.
// 🚩 Call and Text are real anchors, not `location.href = 'tel:…'`: the Android shell and iOS hand
// an anchor tap to the dialer / Messages, and that is the path the old sms: links already took.

const W = 248; // menu width in px; kept in sync with the w-[248px] class below

interface Props {
  link: ContactLink;
  /** The tapped link's box on screen: its centre line and its top and bottom edges. */
  anchor: { x: number; top: number; bottom: number };
  onClose: () => void;
  onNewMessage: (address: string) => void;
  onForward: (address: string) => void;
  /** Copy a street address to the clipboard (2026-10-30); the parent toasts the result. */
  onCopyAddress: (address: string) => void;
}

export function ContactActionPopover({ link, anchor, onClose, onNewMessage, onForward, onCopyAddress }: Props) {
  const panel = useRef<HTMLDivElement>(null);
  // The parent passes a fresh onClose each render; focus is placed once, on open, not on every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.querySelector<HTMLElement>('a, button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { closeRef.current(); return; }
      handleTrapKey(e, panel.current);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, []);

  // Open under the link, kept on screen; above it when there's no room below.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const left = Math.max(8, Math.min(anchor.x - W / 2, vw - W - 8));
  const below = anchor.bottom + 6;
  const style = below + 150 > vh ? { left, bottom: Math.max(8, vh - anchor.top + 6) } : { left, top: below };

  const item = 'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-bmail-text hover:bg-bmail-bg focus:bg-bmail-bg focus:outline-none';
  const label = link.kind === 'phone' ? link.display : link.address;

  return (
    <div className="fixed inset-0 z-[90]" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panel} role="menu" aria-label={label} tabIndex={-1} style={style}
        className="fixed w-[248px] rounded-xl border border-bmail-border bg-bmail-surface p-1.5 shadow-2xl focus:outline-none">
        <div className="truncate px-3 pb-1.5 pt-1 text-xs text-bmail-text-secondary" title={label}>{label}</div>
        {link.kind === 'phone' ? (
          <>
            <a role="menuitem" href={`tel:${link.digits}`} onClick={onClose} className={item}>
              <Phone className="h-4 w-4 shrink-0 text-bmail-gold" /> Call this number
            </a>
            <a role="menuitem" href={`sms:${link.digits}`} onClick={onClose} className={item}>
              <MessageSquare className="h-4 w-4 shrink-0 text-bmail-gold" /> Text this number
            </a>
          </>
        ) : link.kind === 'postal' ? (
          <>
            {/* 🚩 Real anchors like Call/Text above: iOS opens Maps for maps.apple.com, Android
                offers the Maps app for the google.com URL — no window.open needed in any shell. */}
            <a role="menuitem" href={`https://maps.apple.com/?q=${encodeURIComponent(link.address)}`} onClick={onClose} className={item}>
              <Map className="h-4 w-4 shrink-0 text-bmail-gold" /> Open in Apple Maps
            </a>
            <a role="menuitem" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(link.address)}`} onClick={onClose} className={item}>
              <Globe className="h-4 w-4 shrink-0 text-bmail-gold" /> Open in Google Maps
            </a>
            <button role="menuitem" onClick={() => onCopyAddress(link.address)} className={item}>
              <Copy className="h-4 w-4 shrink-0 text-bmail-gold" /> Copy address
            </button>
          </>
        ) : (
          <>
            <button role="menuitem" onClick={() => onNewMessage(link.address)} className={item}>
              <PenSquare className="h-4 w-4 shrink-0 text-bmail-gold" /> New message to this address
            </button>
            <button role="menuitem" onClick={() => onForward(link.address)} className={item}>
              <Forward className="h-4 w-4 shrink-0 text-bmail-gold" /> Forward an email to this address
            </button>
          </>
        )}
      </div>
    </div>
  );
}
